import * as React from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import { AuthUserType } from '@sdk/authentication/types/AuthUserType';
import { CurrencyType } from '@sdk/common/currency/types/CurrencyType';
import { BillingInfoType } from '@sdk/account/api/AccountApi';
import ToastComponent from '@sdk/common/toast/ToastComponent';

import getConfig from '@web/config/getConfig';
import { SubscriptionUtilsType } from '@web/account/types';
import PaymentDetails from '@web/account/purchase/PaymentDetails/PaymentDetails';
import PlanDetails from '@web/account/purchase/PlanDetails/PlanDetails';
import { PurchaseViewContainer } from '@web/account/purchase/PurchaseView/purchaseView.styles';
import { usePurchaseView } from '@web/account/purchase/PurchaseView/purchaseView.hooks';
import { STRIPE_ELEMENTS_DEFAULT_OPTIONS } from '@web/account/constants';

const config = getConfig();
const stripePromise = loadStripe(config.STRIPE_PUBLIC_KEY);

export type Props = {
  currentUser: AuthUserType;
  subscriptionUtils: SubscriptionUtilsType;
  currency: CurrencyType;
  postSubscriptionPurchase: (subscription: BillingInfoType) => Promise<void>;
  areYearlySubscriptionsEnabled: boolean;
  isOldPremium: boolean;
};

const PurchaseView: React.FC<React.PropsWithChildren<Props>> = ({
  currentUser,
  subscriptionUtils,
  currency,
  postSubscriptionPurchase,
  areYearlySubscriptionsEnabled,
  isOldPremium,
}) => {
  const {
    billedOnDate,
    isShopifyBilling,
    monthlyPricePerUserPerMonth,
    priceSummary,
    yearlyPlanDiscount,
    yearlyPricePerUserPerMonth,
  } = usePurchaseView({
    currentUser,
    subscriptionUtils,
  });

  const stripeElementsOptions = {
    amount: Math.max(0, Math.floor(priceSummary.totalNetPrice * 100)),
    mode: 'subscription',
    currency: 'usd',
    ...STRIPE_ELEMENTS_DEFAULT_OPTIONS,
  } as StripeElementsOptions;

  return (
    <Elements stripe={stripePromise} options={stripeElementsOptions}>
      <PurchaseViewContainer mTop={20} isSingleColumn={isShopifyBilling}>
        <PaymentDetails
          currency={currency}
          monthlyPricePerUserPerMonth={monthlyPricePerUserPerMonth}
          yearlyPricePerUserPerMonth={yearlyPricePerUserPerMonth}
          yearlyPlanDiscount={yearlyPlanDiscount}
          areYearlySubscriptionsEnabled={areYearlySubscriptionsEnabled}
          isOldPremium={isOldPremium}
          isShopifyBilling={isShopifyBilling}></PaymentDetails>
        <PlanDetails
          yearlyPlanDiscount={yearlyPlanDiscount}
          monthlyPricePerUserPerMonth={monthlyPricePerUserPerMonth}
          currency={currency}
          priceSummary={priceSummary}
          billedOnDate={billedOnDate}
          postSubscriptionPurchase={postSubscriptionPurchase}
          areYearlySubscriptionsEnabled={areYearlySubscriptionsEnabled}
          currentUser={currentUser}
          isShopifyBilling={isShopifyBilling}></PlanDetails>
        <ToastComponent />
      </PurchaseViewContainer>
    </Elements>
  );
};

export default PurchaseView;
